<script lang="ts">
	import { onMount } from 'svelte';
	import { appStore } from '../../stores/appStore';
	import { get } from 'svelte/store';

	let sessionsByCountry: Array<{ name: string; count: number; percentage: number }> = $state([]);
	let sessionsByOS: Array<{ name: string; count: number; percentage: number }> = $state([]);
	let sessionsByBrowser: Array<{ name: string; count: number; percentage: number }> = $state([]);
	let totalSessions = $state(0);
	let loading = $state(true);
	let error = $state('');

	onMount(async () => {
		await fetchSessionStats();
	});

	async function fetchSessionStats() {
		try {
			loading = true;
			error = '';
			const client = get(appStore).client;
			const token = get(appStore).token;

			if (!client || !token) {
				error = 'Not authenticated';
				return;
			}

			const response = await client.getSessionStats({ token });

			if (response.response.successFull) {
				sessionsByCountry = response.response.byCountry.map(item => ({
					name: item.name,
					count: item.count,
					percentage: Math.round(item.percentage)
				}));

				sessionsByOS = response.response.byOS.map(item => ({
					name: item.name,
					count: item.count,
					percentage: Math.round(item.percentage)
				}));

				sessionsByBrowser = response.response.byBrowser.map(item => ({
					name: item.name,
					count: item.count,
					percentage: Math.round(item.percentage)
				}));

				totalSessions = response.response.totalActiveSessions;
			} else {
				error = response.response.reason || 'Failed to load session stats';
			}
		} catch (e) {
			console.error('Error fetching session stats:', e);
			error = 'Failed to load session statistics';
		} finally {
			loading = false;
		}
	}
</script>

<div class="session-stats">
	{#if loading}
		<div class="loading-state">Loading session statistics...</div>
	{:else if error}
		<div class="error-state">{error}</div>
	{:else}
		<div class="stats-grid">
			<!-- Sessions by Country -->
			<div class="stat-card">
				<div class="card-header">
					<h3>Active Sessions by Country</h3>
					<span class="total">{totalSessions.toLocaleString()} sessions</span>
				</div>
				<div class="stat-list">
					{#if sessionsByCountry.length > 0}
						{#each sessionsByCountry as item}
							<div class="stat-item">
								<div class="stat-info">
									<span class="stat-label">{item.name}</span>
									<span class="stat-value">{item.count.toLocaleString()}</span>
								</div>
								<div class="stat-bar">
									<div class="stat-fill" style="width: {item.percentage}%"></div>
								</div>
								<span class="stat-percent">{item.percentage}%</span>
							</div>
						{/each}
					{:else}
						<div class="empty-state">No data available</div>
					{/if}
				</div>
			</div>

			<!-- Sessions by OS -->
			<div class="stat-card">
				<div class="card-header">
					<h3>Sessions by Operating System</h3>
					<span class="total">{totalSessions.toLocaleString()} sessions</span>
				</div>
				<div class="stat-list">
					{#if sessionsByOS.length > 0}
						{#each sessionsByOS as item}
							<div class="stat-item">
								<div class="stat-info">
									<span class="stat-label">{item.name}</span>
									<span class="stat-value">{item.count.toLocaleString()}</span>
								</div>
								<div class="stat-bar">
									<div class="stat-fill os" style="width: {item.percentage}%"></div>
								</div>
								<span class="stat-percent">{item.percentage}%</span>
							</div>
						{/each}
					{:else}
						<div class="empty-state">No data available</div>
					{/if}
				</div>
			</div>

			<!-- Sessions by Browser -->
			<div class="stat-card">
				<div class="card-header">
					<h3>Sessions by Browser</h3>
					<span class="total">{totalSessions.toLocaleString()} sessions</span>
				</div>
				<div class="stat-list">
					{#if sessionsByBrowser.length > 0}
						{#each sessionsByBrowser as item}
							<div class="stat-item">
								<div class="stat-info">
									<span class="stat-label">{item.name}</span>
									<span class="stat-value">{item.count.toLocaleString()}</span>
								</div>
								<div class="stat-bar">
									<div class="stat-fill browser" style="width: {item.percentage}%"></div>
								</div>
								<span class="stat-percent">{item.percentage}%</span>
							</div>
						{/each}
					{:else}
						<div class="empty-state">No data available</div>
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>

<style>
	.session-stats {
		width: 100%;
	}

	.stats-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: 1.5rem;
	}

	.stat-card {
		background: var(--bg-secondary, #161b22);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 8px;
		padding: 1.5rem;
	}

	.card-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 1.5rem;
		padding-bottom: 1rem;
		border-bottom: 1px solid var(--border-primary, #30363d);
	}

	.card-header h3 {
		margin: 0;
		font-size: 1.125rem;
		font-weight: 600;
		color: var(--text-primary, #e6edf3);
	}

	.total {
		font-size: 0.875rem;
		color: var(--text-secondary, #8b949e);
	}

	.stat-list {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.stat-item {
		display: grid;
		grid-template-columns: 1fr auto;
		gap: 1rem;
		align-items: center;
	}

	.stat-info {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 0.5rem;
	}

	.stat-label {
		font-size: 0.875rem;
		color: var(--text-primary, #e6edf3);
		font-weight: 500;
	}

	.stat-value {
		font-size: 0.875rem;
		color: var(--text-secondary, #8b949e);
	}

	.stat-bar {
		grid-column: 1 / 2;
		height: 6px;
		background: var(--bg-tertiary, #1c2128);
		border-radius: 3px;
		overflow: hidden;
	}

	.stat-fill {
		height: 100%;
		background: var(--accent-primary, #58a6ff);
		border-radius: 3px;
		transition: width 0.3s ease;
	}

	.stat-fill.os {
		background: #7eb845;
	}

	.stat-fill.browser {
		background: #c961ff;
	}

	.stat-percent {
		grid-column: 2 / 3;
		grid-row: 1 / 2;
		font-size: 0.875rem;
		color: var(--text-secondary, #8b949e);
		font-weight: 600;
		min-width: 45px;
		text-align: right;
	}

	.loading-state,
	.error-state,
	.empty-state {
		padding: 2rem;
		text-align: center;
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}

	.error-state {
		color: #f85149;
	}

	.empty-state {
		padding: 1.5rem;
	}
</style>
